import { Injectable, EventEmitter, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
  AbstractControl,
  ValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { DynamicFormInputTypes } from '../../models/dynamic-form-input-types';
import * as momentImport from 'moment-timezone'; // Necessary import to allow eval() to work properly
import { IDynamicFormControl } from '../../models/dynamic-form-control';
import { IDynamicForm, DynamicFormValidators } from '../../models/dynamic-form';
import { IDynamicFormGroupDefinition } from '../../models/dynamic-form-group';
import { IControlNameExpression } from '../../models/control-name-expression';
import { ValidatorFnService } from '../form-utility/validator-fn.service';
import { ApiUrls } from '../../../utility/apiurls-shared';

// eslint-disable-next-line func-style
function evalFormExpression(formGroup: UntypedFormGroup, expression: string): any {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return (function (formGroup: UntypedFormGroup) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const moment = momentImport; // Do not remove this moment declaration - it is needed to eval dates in the expressionLiteral
    // Note to Developers: if an error is coming from the following line, check your GetAdditionalValidationExpressions()
    // and GetRequiredInputs() logic. A common solution is to use "!= null" instead of ".HasValue"
    try {
      return eval(expression);
    } catch (e) {
      console.error('Error evaluating expression. Expression listed below:', e);
      console.log(expression); // eslint-disable-line no-console
    }
  })(formGroup);
}

// Puts together the FormGroupDefinition from the DynamicForm retrieved from the server
@Injectable()
export class DynamicFormsService {
  private httpClient = inject(HttpClient);
  private fb = inject(UntypedFormBuilder);
  private validatorFnService = inject(ValidatorFnService);
  private apiUrls = inject(ApiUrls);

  isIE = false;
  selectChange = new EventEmitter();
  keepMoment = momentImport;

  
  // Used to keep the moment import when minimizing/stripping TS

  constructor() {
    this.isIE = /Trident\/|MSIE/.test(window.navigator.userAgent);
  }

  cachedApiCalls: Observable<any>[] = [];

  private getDynamicFormForModel(
    apiModelName: string,
  ): Observable<{ formName: string; dynamicForm: IDynamicForm }> {
    const uri = this.apiUrls.forms?.[apiModelName]?.();
    if (uri == null) {
      console.error('Unable to find apiUrl for modelName: ' + apiModelName);
      return;
    }
    if (!this.cachedApiCalls[apiModelName]) {
      this.cachedApiCalls[apiModelName] = this.httpClient.get<any>(uri).pipe(shareReplay(1));
    }
    return (this.cachedApiCalls[apiModelName] as Observable<any>).pipe(
      map(form => {
        if (form.formControls != null) {
          return [{ formName: apiModelName, dynamicForm: form }];
        } else {
          const arr = [];
          for (const newModelName in form) {
            if (form.hasOwnProperty(newModelName)) {
              const newForm = form[newModelName];
              arr.push({
                formName: newModelName,
                dynamicForm: newForm,
              });
            }
          }
          return arr;
        }
      }),
      map(formGroups => formGroups.find(g => g.formName === apiModelName)),
    );
  }

  getFormGroupForModel(apiModelName: string): Observable<IDynamicFormGroupDefinition> {
    return this.getDynamicFormForModel(apiModelName).pipe(
      map(dynamicForm => this.convertApiResponseToFormGroupDef(dynamicForm.dynamicForm)),
    );
  }

  getFormGroupSubset(
    formGroup: UntypedFormGroup,
    validatedFields: string[],
    nonValidatedFields: string[],
  ): IDynamicFormGroupDefinition {
    const formGroupDef = <IDynamicFormGroupDefinition>formGroup['dynamicFormGroupDefinition'];
    return this.convertApiResponseToFormGroupDef(
      formGroupDef.formInfo,
      validatedFields,
      nonValidatedFields,
    );
  }

  private convertApiResponseToFormGroupDef(
    response: IDynamicForm,
    validatedFields?: string[],
    nonValidatedFields?: string[],
    formGroup?: UntypedFormGroup,
  ): IDynamicFormGroupDefinition {
    const selectedFields = validatedFields
      ? validatedFields.concat(nonValidatedFields || [])
      : nonValidatedFields;
    const overallFormValidators = selectedFields
      ? null
      : {
          /* eslint-disable indent */
          validators: response.fullValidators.map(val => {
            if (val === DynamicFormValidators.AtLeastOneRequired) {
              const excludeList = response.formControls
                .filter(c => c.excludeFromAtLeastOneRequired)
                .map(c => c.controlName);
              return this.validatorFnService.atLeastOneFieldValidator(excludeList);
            } else if (val === DynamicFormValidators.ExactNumberOfFieldsRequired) {
              const includeList = response.formControls.filter(
                c => c.includeForExactNumberOfFieldsCount,
              );
              const numberOfFieldsRequired = includeList?.[0]?.numberOfFieldsRequired;
              return this.validatorFnService.exactNumberOfFieldsValidator(
                numberOfFieldsRequired,
                includeList.map(c => c.controlName),
              );
            }
          }),
        };
    /* eslint-enable indent */

    if (!formGroup) {
      formGroup = this.fb.group({}, overallFormValidators);
    }

    const selectedFieldsVariableList = selectedFields ? new Set<string>() : null;
    const expressions = {};
    for (const exprName in response.expressions) {
      if (selectedFields && !selectedFields.includes(exprName)) {
        continue;
      }

      const expression = this.convertCsEquationToJs(response.expressions[exprName], 'formGroup');
      if (selectedFieldsVariableList) {
        expression.variableList.forEach(v => selectedFieldsVariableList.add(v));
      }
      expressions[exprName] = function (): any {
        return evalFormExpression(formGroup, expression.jsExpression);
      };
    }

    formGroup['expressions'] = expressions;

    const controlInfo = response.formControls
      .filter(fc => !selectedFields || selectedFields.includes(fc.controlName))
      .map(control => {
        let validators: ValidatorFn[];
        const variableList = new Set<string>();
        if (validatedFields && !validatedFields.includes(control.controlName)) {
          validators = [];
        } else {
          validators = this.createValidators(control);

          const controlRequiredExpressions =
            response.requiredExpressions?.filter(ave => ave.controlName === control.controlName) ||
            [];
          const requiredInputValidators = this.getRequiredInputValidators(
            formGroup,
            controlRequiredExpressions,
          );

          const controlAdditionalValidationExpressions =
            response.additionalValidationExpressions?.filter(
              ave => ave.controlName === control.controlName,
            ) || [];
          const additionalValidators = this.getAdditionalValidationAndErrors(
            formGroup,
            controlAdditionalValidationExpressions,
          );

          const allValidators = requiredInputValidators.concat(additionalValidators);
          validators.push(...allValidators.map(ri => ri.validator));
          allValidators
            .map(ri => ri.variableList)
            .forEach(varList => varList.forEach(varName => variableList.add(varName)));

          const additionalErrors: ValidationErrors = controlRequiredExpressions
            .concat(controlAdditionalValidationExpressions)
            .map(cne => ({ key: cne.key, message: cne.message }))
            .reduce((addErrs, addErr) => {
              addErrs[addErr.key] = addErr.message;
              return addErrs;
            }, {});

          variableList.delete(control.controlName);
          if (selectedFieldsVariableList) {
            variableList.forEach(v => selectedFieldsVariableList.add(v));
          }

          control.additionalErrors = additionalErrors;
        }

        return {
          controlName: control.controlName,
          dynamicControl: control,
          formControl: this.fb.control(null, validators),
          variableList,
        };
      });

    if (selectedFields) {
      selectedFieldsVariableList.forEach(dependencyVar => {
        const exists = formGroup.contains(dependencyVar);
        const newControl = this.fb.control('');
        if (exists) {
          formGroup.setControl(dependencyVar, newControl);
        } else {
          formGroup.addControl(dependencyVar, newControl);
        }
      });
    }

    controlInfo.forEach(ci => {
      const formControl = ci.formControl;
      formControl['dynamicFormControl'] = ci.dynamicControl;

      const exists = formGroup.contains(ci.controlName);
      if (exists) {
        formGroup.setControl(ci.controlName, formControl);
      } else {
        formGroup.addControl(ci.controlName, formControl);
      }
    });

    Object.keys(formGroup.controls).forEach(controlName => {
      const dependentControls = controlInfo.filter(
        otherCi => otherCi.controlName !== controlName && otherCi.variableList?.has(controlName),
      );

      if (dependentControls.length) {
        const formControl = formGroup.controls[controlName];
        formControl.valueChanges.subscribe(() => {
          dependentControls.forEach(dc =>
            dc.formControl.updateValueAndValidity({ emitEvent: false }),
          );
        });
      }
    });

    const formInfo: IDynamicFormGroupDefinition = {
      formControlInfo: response.formControls,
      formGroup,
      formInfo: response,
    };
    formGroup['dynamicFormGroupDefinition'] = formInfo;
    return formInfo;
  }

  findControl = (
    formInfo: { formControlInfo: IDynamicFormControl[] },
    controlName: string,
  ): IDynamicFormControl =>
    formInfo?.formControlInfo?.find(
      (value: IDynamicFormControl) => value.controlName === controlName,
    );

  private createValidators(formControlInfo: IDynamicFormControl): ValidatorFn[] {
    const validators: ValidatorFn[] = [];

    if (formControlInfo.required) {
      this.addRequiredValidator(formControlInfo, validators);
    }
    if (formControlInfo.requiredTrue) {
      this.addRequiredTrueValidator(formControlInfo, validators);
    }
    if (formControlInfo.minLength != null) {
      validators.push(Validators.minLength(formControlInfo.minLength));
    }
    if (formControlInfo.maxLength != null) {
      validators.push(Validators.maxLength(formControlInfo.maxLength));
    }
    if (formControlInfo.minValue != null) {
      this.addMinValidator(formControlInfo, validators);
    }
    if (formControlInfo.maxValue != null) {
      this.addMaxValidator(formControlInfo, validators);
    }
    if (formControlInfo.regularExpression != null) {
      validators.push(Validators.pattern(formControlInfo.regularExpression));
    }
    if (formControlInfo.type === DynamicFormInputTypes.email) {
      validators.push(Validators.email);
    }

    return validators;
  }

  private addRequiredValidator(formControlInfo: any, validators: ValidatorFn[]) {
    if (formControlInfo.type === DynamicFormInputTypes.date) {
      validators.push(this.validatorFnService.requiredDateValidator());
    } else {
      validators.push(Validators.required);
    }
  }

  private addRequiredTrueValidator(formControlInfo: any, validators: ValidatorFn[]) {
    if (
      formControlInfo.type === DynamicFormInputTypes.checkbox ||
      formControlInfo.type === DynamicFormInputTypes.toggle
    ) {
      validators.push(Validators.requiredTrue);
    } else {
      console.error('Required True validator should only be used with a boolean input');
    }
  }

  private addMinValidator(formControlInfo: any, validators: ValidatorFn[]) {
    if (formControlInfo.type === DynamicFormInputTypes.date) {
      validators.push(this.validatorFnService.minDateValidator(formControlInfo.minValue));
    } else {
      validators.push(Validators.min(formControlInfo.minValue));
    }
  }

  private addMaxValidator(formControlInfo: any, validators: ValidatorFn[]) {
    if (formControlInfo.type === DynamicFormInputTypes.date) {
      validators.push(this.validatorFnService.maxDateValidator(formControlInfo.maxValue));
    } else {
      validators.push(Validators.max(formControlInfo.maxValue));
    }
  }

  private getRequiredInputValidators(
    formGroup: UntypedFormGroup,
    requiredExpressions: IControlNameExpression[],
  ) {
    return requiredExpressions.map(requiredExpression => {
      const expr = this.convertCsEquationToJs(requiredExpression.expression, 'formGroup');
      return {
        validator: this.createRequiredValidator(
          formGroup,
          expr.jsExpression,
          requiredExpression.message,
        ),
        variableList: expr.variableList,
      };
    });
  }

  // If the conditional required function is true, set validators accordingly, else use the default
  private createRequiredValidator(
    formGroup: UntypedFormGroup,
    expressionLiteral: string,
    errorMessage: string,
  ): ValidatorFn {
    return this.createAdditionalValidationValidator(
      formGroup,
      expressionLiteral,
      this.validatorFnService.noWhiteSpaceValidator(errorMessage),
      true,
    );
  }

  private getAdditionalValidationAndErrors(
    formGroup: UntypedFormGroup,
    additionalValidationExpressions: IControlNameExpression[],
  ): { validator: ValidatorFn; variableList: Set<string> }[] {
    return additionalValidationExpressions.map(targetValidation => {
      // Gather information to generate the required functions
      const expression = this.convertCsEquationToJs(targetValidation.expression, 'formGroup');
      const invertedExpression = `!( ${expression.jsExpression} )`;
      return {
        validator: this.createAdditionalValidationValidator(
          formGroup,
          invertedExpression,
          this.validatorFnService.alwaysInvalidValidator(
            targetValidation.key,
            targetValidation.message,
          ),
          false,
        ),
        variableList: expression.variableList,
      };
    });
  }

  // If expression is true, add the validatorFn on top of existing validations
  private createAdditionalValidationValidator(
    formGroup: UntypedFormGroup,
    expressionLiteral: string,
    validator: ValidatorFn,
    isRequiredValidator: boolean,
  ): ValidatorFn {
    return (control: AbstractControl) => {
      let evalResult: boolean;
      if (isRequiredValidator === false && (control.value == null || control.value === '')) {
        evalResult = false;
      } else {
        evalResult = evalFormExpression(formGroup, expressionLiteral);
      }
      if (evalResult) {
        if (isRequiredValidator) {
          control['condrequired'] = true;
        }
        return validator(control);
      } else {
        if (isRequiredValidator) {
          control['condrequired'] = false;
        }
        return null;
      }
    };
  }

  // Function that edits an expression literal from EquationBuilder on the C# side to a JS expression as a string
  private convertCsEquationToJs(
    rawExpression: string,
    groupVariableName: string,
  ): { jsExpression: string; variableList: Set<string> } {
    let beginIndex = -1;
    let jsExpression = rawExpression;
    const variableList = new Set<string>();

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // End Condition
      beginIndex = jsExpression.indexOf('{{');
      if (beginIndex < 0) {
        break;
      }

      // Replace {{}} with a variable expression
      jsExpression = jsExpression.replace(/{{(.+?)}}/, (substr: string) => {
        const variableName = substr.substring(2, substr.length - 2);
        variableList.add(variableName);
        if (this.isIE) {
          return `(${groupVariableName}.get("${variableName}") == null ? null : ${groupVariableName}.get("${variableName}").value)`;
        } else {
          return `${groupVariableName}.get("${variableName}")?.value`;
        }
      });
    }

    return { jsExpression, variableList };
  }
}
